<template>
  <div class="min-h-screen bg-[#FFF7EF]">
    <div class="mx-auto max-w-6xl px-4 py-12 lg:px-8">
      <div class="grid gap-8 lg:grid-cols-[1fr_1fr]">
        <section class="rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
          <RouterLink class="back-home-link" to="/">
            <i class="ri-arrow-left-line"></i>
            <span>Back to home</span>
          </RouterLink>

          <h1 class="text-3xl font-semibold text-ink">Create your Invi8 account</h1>
          <p class="mt-2 text-sm text-muted">Join as a vendor or switch to user registration.</p>

          <AuthRoleTabs
            class="mt-6"
            active-role="vendor"
            left-to="/register?role=user"
            right-to="/vendor-signup?role=vendor"
            @select="resetForm"
          />

          <form class="mt-6 space-y-4" @submit.prevent="submit">
            <p v-if="submitError" class="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-semibold text-red-700">
              {{ submitError }}
            </p>

            <label class="field-block">
              <span class="sr-only">Vendor Name</span>
              <input
                v-model.trim="form.brandName"
                type="text"
                required
                placeholder="Vendor Name"
                class="register-field"
              >
              <span v-if="errors.brandName" class="error-text">{{ errors.brandName }}</span>
            </label>

            <label class="field-block">
              <span class="sr-only">Email</span>
              <input
                v-model.trim="form.email"
                type="email"
                required
                placeholder="Email"
                class="register-field"
              >
              <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
            </label>

            <label class="field-block">
              <span class="sr-only">Phone Number</span>
              <div class="phone-shell">
                <input
                  ref="phoneInputRef"
                  type="tel"
                  class="register-field register-field--phone"
                  @input="syncPhone"
                  @blur="syncPhone"
                >
              </div>
              <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
            </label>

            <label class="field-block">
              <span class="sr-only">Password</span>
              <div class="password-shell">
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  placeholder="Password"
                  class="register-field register-field--with-action"
                >
                <div class="password-actions">
                  <button
                    type="button"
                    class="icon-btn"
                    @click="generateStrongPassword"
                    aria-label="Generate password"
                    title="Generate password"
                  >
                    <i class="ri-shuffle-line"></i>
                  </button>
                  <button
                    type="button"
                    class="icon-btn"
                    @click="showPassword = !showPassword"
                    aria-label="Toggle password visibility"
                    title="Toggle password visibility"
                  >
                    <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>
              <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
            </label>

            <div class="strength-card">
              <div class="strength-head">
                <strong>Password strength:</strong>
                <span>{{ passwordStrengthLabel }}</span>
              </div>
              <div class="strength-bar">
                <span :style="{ width: `${passwordStrengthScore * 20}%` }"></span>
              </div>
              <div class="strength-rules">
                <p :class="ruleClass(passwordChecks.minLength)">At least 8 characters</p>
                <p :class="ruleClass(passwordChecks.uppercase)">Contains uppercase letter</p>
                <p :class="ruleClass(passwordChecks.lowercase)">Contains lowercase letter</p>
                <p :class="ruleClass(passwordChecks.number)">Contains number</p>
                <p :class="ruleClass(passwordChecks.special)">Contains special character</p>
              </div>
            </div>

            <label class="field-block">
              <span class="sr-only">Confirm Password</span>
              <div class="password-shell">
                <input
                  v-model="form.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  placeholder="Confirm Password"
                  class="register-field register-field--with-action"
                >
                <div class="password-actions">
                  <button
                    type="button"
                    class="icon-btn"
                    @click="showConfirmPassword = !showConfirmPassword"
                    aria-label="Toggle confirm password visibility"
                    title="Toggle confirm password visibility"
                  >
                    <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                  </button>
                </div>
              </div>
              <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
            </label>

            <button
              type="submit"
              :disabled="loading"
              class="register-submit"
            >
              {{ loading ? 'Registering...' : 'Register' }}
            </button>
          </form>

          <p class="mt-6 text-center text-xs text-muted">
            Already have an account?
            <RouterLink class="font-semibold text-brand" to="/login?role=vendor">Log in</RouterLink>
          </p>
        </section>

        <section class="rounded-3xl border border-orange-100 bg-white/90 p-8 shadow-sm">
          <p class="text-xs font-semibold uppercase tracking-[0.2em] text-muted">Vendor access</p>
          <h2 class="mt-3 text-2xl font-semibold text-ink">Set up your vendor account with the same quick flow</h2>
          <div class="mt-6 space-y-4 text-sm text-muted">
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Keep enquiries in one place</p>
                <p class="text-xs">Manage incoming leads and business details from a single account.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Stay reachable</p>
                <p class="text-xs">Use your email and phone so users can connect with your business faster.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E5] text-lg">✓</span>
              <div>
                <p class="font-semibold text-ink">Start with a secure profile</p>
                <p class="text-xs">Use a strong password and complete onboarding after signup.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import Swal from 'sweetalert2'
import AuthRoleTabs from '@/components/user/AuthRoleTabs.vue'
import api from '@/services/vendor/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()

const form = reactive({
  brandName: '',
  email: '',
  phone: '',
  phoneRaw: '',
  password: '',
  confirmPassword: ''
})

const errors = reactive({})
const phoneInputRef = ref(null)
const phoneIti = ref(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const loading = ref(false)
const submitError = ref('')

const passwordChecks = computed(() => ({
  minLength: form.password.length >= 8,
  uppercase: /[A-Z]/.test(form.password),
  lowercase: /[a-z]/.test(form.password),
  number: /\d/.test(form.password),
  special: /[^A-Za-z0-9]/.test(form.password)
}))

const passwordStrengthScore = computed(() =>
  Object.values(passwordChecks.value).filter(Boolean).length
)

const passwordStrengthLabel = computed(() => {
  if (!form.password) return 'Enter A Password'
  if (passwordStrengthScore.value <= 2) return 'Weak'
  if (passwordStrengthScore.value <= 4) return 'Medium'
  return 'Strong'
})

const ruleClass = (passed) => ({
  'rule-item': true,
  'rule-item--pass': passed,
  'rule-item--fail': !passed
})

const validateForm = () => {
  Object.keys(errors).forEach((key) => delete errors[key])
  const phoneDigits = (form.phoneRaw || '').replace(/\D/g, '')
  const utilsLoaded = typeof window !== 'undefined' && typeof window.intlTelInputUtils !== 'undefined'

  if (!form.brandName) errors.brandName = 'Vendor name is required'
  if (!form.email) errors.email = 'Email is required'
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email'
  if (!form.phoneRaw) errors.phone = 'Phone number is required'
  if (phoneDigits && phoneDigits.length > 15) errors.phone = 'Phone number must be 15 digits or less'
  if (phoneDigits && phoneDigits.length < 6) errors.phone = 'Enter a valid phone number'
  if (phoneDigits && utilsLoaded && phoneIti.value && form.phone && !phoneIti.value.isValidNumber()) errors.phone = 'Enter a valid phone number'
  if (!form.password) errors.password = 'Password is required'
  if (form.password && form.password.length < 8) errors.password = 'Password must be at least 8 characters'
  if (form.password && passwordStrengthScore.value < 5) errors.password = 'Use uppercase, lowercase, number and special character'
  if (!form.confirmPassword) errors.confirmPassword = 'Confirm password is required'
  if (form.password && form.confirmPassword && form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match'

  return Object.keys(errors).length === 0
}

const generateStrongPassword = () => {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*_-+='
  const all = uppercase + lowercase + numbers + symbols
  const chars = [
    uppercase[Math.floor(Math.random() * uppercase.length)],
    lowercase[Math.floor(Math.random() * lowercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ]
  while (chars.length < 12) {
    chars.push(all[Math.floor(Math.random() * all.length)])
  }
  form.password = chars.sort(() => Math.random() - 0.5).join('')
  form.confirmPassword = form.password
}

const resetForm = () => {
  form.brandName = ''
  form.email = ''
  form.phone = ''
  form.phoneRaw = ''
  form.password = ''
  form.confirmPassword = ''
  submitError.value = ''
  showPassword.value = false
  showConfirmPassword.value = false
  Object.keys(errors).forEach((key) => delete errors[key])
  if (phoneInputRef.value) {
    phoneInputRef.value.value = ''
  }
}

const syncPhone = () => {
  if (!phoneInputRef.value) return
  const digits = (phoneInputRef.value.value || '').replace(/\D/g, '').slice(0, 15)
  form.phoneRaw = digits

  if (phoneIti.value && digits) {
    const dialCode = phoneIti.value.getSelectedCountryData?.()?.dialCode || ''
    const normalized = dialCode ? `+${dialCode}${digits}` : digits
    form.phone = normalized
    if (phoneInputRef.value.value.replace(/\D/g, '').length > 15) {
      phoneIti.value.setNumber(normalized)
    }
  } else {
    form.phone = ''
  }
}

const getPhonePayload = () => {
  const dialCode = phoneIti.value?.getSelectedCountryData?.()?.dialCode || ''
  return {
    s_phone_code: dialCode ? `+${dialCode}` : '',
    s_phone: (form.phoneRaw || '').replace(/\D/g, '')
  }
}

const submit = async () => {
  syncPhone()
  if (!validateForm()) return
  submitError.value = ''
  loading.value = true

  const payload = new FormData()
  const phonePayload = getPhonePayload()
  payload.append('s_email', form.email)
  payload.append('s_phone_code', phonePayload.s_phone_code)
  payload.append('s_phone', phonePayload.s_phone)
  payload.append('s_name', form.brandName)
  payload.append('s_password', form.password)

  try {
    const { data } = await api.registerVendor(payload)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.message || 'Vendor registration failed')
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const responseData = data?.data && typeof data.data === 'object' ? data.data : null
    const user =
      data?.user ||
      data?.data?.user ||
      data?.data?.profile ||
      data?.profile ||
      (responseData
        ? {
            s_id: responseData.s_id,
            s_name: responseData.name || form.brandName,
            s_email: responseData.email || form.email,
            s_phone: responseData.phone || phonePayload.s_phone,
            s_phone_code: responseData.phone_code || phonePayload.s_phone_code
          }
        : null)

    if (token) {
      setAuthToken(token)
    } else {
      setAuthSession()
    }

    if (user) {
      setAuthUser({ ...user, role: 'vendor', type: 'vendor' })
    } else {
      setAuthUser({
        s_name: form.brandName,
        s_email: form.email,
        s_phone_code: phonePayload.s_phone_code,
        s_phone: phonePayload.s_phone,
        role: 'vendor',
        type: 'vendor'
      })
    }

    setAuthSession({
      loggedIn: true,
      role: 'vendor',
      type: 'vendor',
      s_id:
        user?.s_id ||
        user?.id ||
        user?.vendor_id ||
        user?.vendorId ||
        ''
    })

    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || data?.message || 'Registered successfully',
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true
    })

    router.push('/dashboard')
  } catch (err) {
    submitError.value = err?.response?.data?.message || err?.message || 'Vendor registration failed'
  } finally {
    loading.value = false
  }
}

const initPhoneInput = () => {
  if (!phoneInputRef.value || phoneIti.value) return
  phoneIti.value = intlTelInput(phoneInputRef.value, {
    initialCountry: 'in',
    separateDialCode: true,
    nationalMode: false,
    preferredCountries: ['in', 'us', 'gb', 'ae'],
    autoPlaceholder: 'polite',
    loadUtils: () => import('intl-tel-input/utils')
  })
  syncPhone()
}

onMounted(() => {
  initPhoneInput()
})

onUnmounted(() => {
  if (phoneIti.value) {
    phoneIti.value.destroy()
    phoneIti.value = null
  }
})
</script>

<style scoped>
.back-home-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 13px;
  font-weight: 700;
  color: #ec2263;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.field-block {
  display: block;
}

.register-field {
  width: 100%;
  border: 1px solid #e8e0d8;
  border-radius: 18px;
  background: #fff;
  padding: 16px 18px;
  font-size: 16px;
  color: #4b5563;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.register-field:focus {
  outline: none;
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.phone-shell {
  position: relative;
}

.register-field--phone {
  padding-left: 122px !important;
}

.register-field--with-action {
  padding-right: 88px;
}

.password-shell {
  position: relative;
}

.password-actions {
  position: absolute;
  top: 50%;
  right: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transform: translateY(-50%);
}

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
}

.strength-card {
  border: 1px solid #f1ece7;
  border-radius: 18px;
  background: #faf9f8;
  padding: 14px 14px 12px;
}

.strength-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #334155;
}

.strength-head span {
  font-weight: 600;
  color: #64748b;
}

.strength-bar {
  margin-top: 12px;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.strength-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #fb923c 0%, #f97316 55%, #ec2263 100%);
  transition: width 0.2s ease;
}

.strength-rules {
  margin-top: 12px;
  display: grid;
  gap: 6px;
}

.rule-item {
  margin: 0;
  font-size: 14px;
}

.rule-item::before {
  content: '×';
  margin-right: 8px;
  font-weight: 700;
}

.rule-item--fail {
  color: #8b93a1;
}

.rule-item--pass {
  color: #0f766e;
}

.rule-item--pass::before {
  content: '✓';
}

.error-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
}

.register-submit {
  width: 100%;
  border: 0;
  border-radius: 999px;
  background: #ffa867;
  padding: 15px 18px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 10px 24px rgba(255, 168, 103, 0.24);
  transition: background 0.2s ease, transform 0.2s ease;
}

.register-submit:hover:not(:disabled) {
  background: #ff9850;
  transform: translateY(-1px);
}

.register-submit:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

:deep(.iti) {
  width: 100%;
  display: block;
  position: relative;
}

:deep(.iti__flag-container) {
  top: 1px;
  bottom: 1px;
  left: 14px;
  width: 96px;
  display: flex;
  align-items: center;
  border-right: 1px solid #d9dee7;
  background: transparent;
}

:deep(.iti__selected-country) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 0;
  padding-bottom: 0;
  background: transparent;
}

:deep(.iti__selected-country-primary) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px 0 8px;
  margin-right: 0;
  position: static;
}

:deep(.iti__selected-country-primary)::after {
  display: none;
}

:deep(.iti__arrow) {
  margin-left: 0;
  border-top-color: #6b7280;
}

:deep(.iti--separate-dial-code .iti__selected-dial-code) {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
  margin-left: 6px;
}

:deep(.iti--separate-dial-code .iti__tel-input) {
  height: 58px;
  min-height: 58px;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 122px !important;
  padding-right: 18px;
  border: 0;
  background: transparent;
  font-size: 15px;
  line-height: 1.2;
  color: #111827;
  box-sizing: border-box;
}

:deep(.iti__country-list) {
  z-index: 60;
  top: calc(100% + 8px);
  left: 0;
  right: auto;
  width: 320px !important;
  min-width: 320px;
  max-width: min(320px, calc(100vw - 32px));
  max-height: 300px;
  margin-top: 0;
  border: 1px solid #e8e0d8;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
}

:deep(.iti__search-container) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  border-bottom: 1px solid #edf0f4;
}

:deep(.iti__search-container)::before {
  content: '\ea6e';
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-family: remixicon;
  font-size: 15px;
  color: #9ca3af;
  pointer-events: none;
}

:deep(.iti__search-input),
:deep(.iti__search-input input),
:deep(.iti input[type='search']) {
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 18px 18px 0 0;
  padding: 0 14px 0 38px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
}

:deep(.iti__search-input:focus),
:deep(.iti__search-input input:focus),
:deep(.iti input[type='search']:focus) {
  outline: none;
}

:deep(.iti__country) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}

:deep(.iti__country-name) {
  color: #111827;
  white-space: normal;
}

:deep(.iti__dial-code) {
  margin-left: auto;
  color: #94a3b8;
}

:deep(.iti__flag) {
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06);
}

:deep(.iti input::placeholder) {
  color: transparent;
}
</style>
